"use client";

import { startTransition, useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { MessageCircle } from "lucide-react";
import { useTranslations } from "next-intl";
import { Link } from "@/navigation";

const ChatWidgetDynamic = dynamic(
  () => import("@/components/ChatWidget"),
  { ssr: false, loading: () => null }
);


export const ValentineCampaignModalDynamic = dynamic(
  () =>
    import("@/components/ValentineCampaignModal").then((mod) => ({
      default: mod.ValentineCampaignModal,
    })),
  { ssr: false, loading: () => null }
);

/**
 * Defers floating homepage UI until the browser is idle or the user starts
 * interacting with the page, keeping fixed-position widgets out of the
 * critical LCP path on mobile.
 */
function useDeferredFloatingUi(timeoutMs: number) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const idleWindow = window as Window & {
      requestIdleCallback?: (
        callback: IdleRequestCallback,
        options?: IdleRequestOptions,
      ) => number;
      cancelIdleCallback?: (handle: number) => void;
    };
    let idleId: number | undefined;
    let timerId: ReturnType<typeof globalThis.setTimeout> | undefined;

    const enable = () => {
      startTransition(() => setReady(true));
    };

    if (typeof idleWindow.requestIdleCallback === "function") {
      idleId = idleWindow.requestIdleCallback(enable, {
        timeout: timeoutMs,
      });
    } else {
      timerId = globalThis.setTimeout(enable, timeoutMs);
    }

    const passiveEvents: Array<keyof WindowEventMap> = ["scroll"];

    for (const eventName of passiveEvents) {
      window.addEventListener(eventName, enable, { once: true, passive: true });
    }

    return () => {
      if (
        idleId !== undefined &&
        typeof idleWindow.cancelIdleCallback === "function"
      ) {
        idleWindow.cancelIdleCallback(idleId);
      }
      if (timerId !== undefined) {
        globalThis.clearTimeout(timerId);
      }
      for (const eventName of passiveEvents) {
        window.removeEventListener(eventName, enable);
      }
    };
  }, [timeoutMs]);

  return { ready, setReady };
}


const FLOATING_BUTTON_CLASS =
  "fixed bottom-24 right-4 z-[60] flex items-center gap-2 rounded-full bg-primary px-4 py-3 text-primary-foreground shadow-lg transition-all duration-200 hover:scale-105 hover:bg-primary/90 active:scale-95 md:bottom-6 md:right-6 lg:bottom-8 lg:right-8";
const FLOATING_BUTTON_STYLE = { bottom: "calc(6rem + env(safe-area-inset-bottom))" } as const;

function DirectOrderButton() {
  const t = useTranslations("chat");
  return (
    <Link
      href="/order"
      prefetch={false}
      className={FLOATING_BUTTON_CLASS}
      style={FLOATING_BUTTON_STYLE}
      aria-label={t("openButtonAriaLabel")}
    >
      <MessageCircle className="h-5 w-5" />
      <span className="font-medium">{t("openButtonText")}</span>
    </Link>
  );
}

function DeferredChatButton() {
  const { ready, setReady } = useDeferredFloatingUi(4000);
  const [openOnLoad, setOpenOnLoad] = useState(false);
  const t = useTranslations("chat");

  if (!ready) {
    return (
      <button
        type="button"
        onClick={() => {
          setOpenOnLoad(true);
          startTransition(() => setReady(true));
        }}
        className={FLOATING_BUTTON_CLASS}
        style={FLOATING_BUTTON_STYLE}
        aria-label={t("openButtonAriaLabel")}
      >
        <MessageCircle className="h-5 w-5" />
        <span className="font-medium">{t("openButtonText")}</span>
      </button>
    );
  }

  return <ChatWidgetDynamic initiallyOpen={openOnLoad} />;
}

export function LazyChatWidget({ directOrderLink = false }: { directOrderLink?: boolean } = {}) {
  // Split into two components so the deferred-UI hook (scroll listener +
  // requestIdleCallback) doesn't run on the static-link path.
  return directOrderLink ? <DirectOrderButton /> : <DeferredChatButton />;
}
